import { useRef, useState, useEffect, useMemo, memo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

const _vecA = new THREE.Vector3();

interface MagicProps {
    id: string;
    active: boolean;
    position: THREE.Vector3;
    targetPoint: THREE.Vector3;
    speed: number;
    startTimeStamp: number;
    onHit?: () => void;
}

export const MagicProjectile = memo(({ id, active, position, targetPoint, speed, startTimeStamp, onHit }: MagicProps) => {
    const groupRef = useRef<THREE.Group>(null);
    const deactivateProjectile = useStore((state) => state.deactivateProjectile);
    const spawnHit = useStore((state) => state.spawnHit);

    const { duration } = useMemo(() => {
        const dist = position.distanceTo(targetPoint);
        return { duration: dist / speed };
    }, [position, targetPoint, speed]);

    const startTime = useRef(startTimeStamp);
    useEffect(() => {
        if (active) {
            startTime.current = startTimeStamp;
        }
    }, [active, startTimeStamp, id]);

    useFrame(() => {
        if (!active || !groupRef.current) return;

        const elapsed = (performance.now() - startTime.current) / 1000;
        const progress = Math.min(elapsed / duration, 1);

        // Update position (Reused vector)
        _vecA.lerpVectors(position, targetPoint, progress);
        groupRef.current.position.copy(_vecA);

        if (progress >= 1) {
            spawnHit([targetPoint.x, targetPoint.y, targetPoint.z], "#00ffff");
            if (onHit) onHit();
            deactivateProjectile(id);
        }
    });

    const { camera } = useThree();
    const camPos = camera ? camera.position : new THREE.Vector3();
    const approxDist = camPos.distanceTo(position);

    return (
        <group ref={groupRef} position={[position.x, position.y, position.z]} visible={active}>
            {/* High-glow sphere acts as fake light (no realtime light overhead) */}
            <mesh>
                <sphereGeometry args={[0.1, 16, 16]} />
                <meshStandardMaterial
                    color="#00ffff"
                    emissive="#00ffff"
                    emissiveIntensity={3.5}
                    toneMapped={true}
                />
            </mesh>
            {active && (
                <Sparkles count={Math.max(4, Math.floor(10 * Math.min(1, 100 / Math.max(1, approxDist))))} scale={0.8} size={2} speed={0.35} opacity={0.6} color="#00ffff" />
            )}
        </group>
    );
});
